import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cloud, KeyRound, Loader2, Lock, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/staffApi";
import { screenMessage } from "../lib/screenMessage";
import useNoIndex from "../lib/useNoIndex";

const ALREADY = "already set up";

/* One-time first owner. The server accepts this only while no owner exists and the setup key matches. */
const AdminSetup = () => {
  const navigate = useNavigate();
  useNoIndex();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setupKey, setSetupKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    let live = true;
    api.get("/admin/bootstrap").then(({ data }) => {
      if (!live) return;
      if (data?.detail === ALREADY) {
        setClosed(true);
        setError(ALREADY);
      }
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/bootstrap/complete", {
        email, password, token: setupKey,
      });
      setDone(data?.next || "Owner account created. Sign in, then set up the authenticator.");
      setSetupKey("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === ALREADY) setClosed(true);
      setError(screenMessage("admin"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-5" data-testid="admin-setup-page">
      <div className="pointer-events-none absolute inset-0 bg-dots opacity-50" />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="flex flex-col items-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Cloud className="h-6 w-6" strokeWidth={2.2} />
          </span>
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground">First owner setup</h1>
          <p className="mt-1 text-center text-sm text-muted-foreground">
            Creates the first owner account. This page works once.
          </p>
        </div>

        {closed && (
          <p className="mt-6 rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="admin-setup-closed">
            already set up
          </p>
        )}

        {done && (
          <div className="mt-6 space-y-3">
            <p className="rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="admin-setup-done">{done}</p>
            <Button className="w-full rounded-full" onClick={() => navigate("/admin/login")}>Continue to staff sign-in</Button>
          </div>
        )}

        {!closed && !done && (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="setup-email" className="text-sm">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="setup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="pl-9" autoComplete="username" required data-testid="admin-setup-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="setup-password" className="text-sm">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="setup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       placeholder="At least 12 characters" className="pl-9"
                       autoComplete="new-password" required data-testid="admin-setup-password" />
              <p className="mt-1 text-xs text-muted-foreground">
                Use an email that does not already have an account.
              </p>
              </div>
            </div>
            <div>
              <Label htmlFor="setup-key" className="text-sm">Setup key</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="setup-key" type="password" value={setupKey} onChange={(e) => setSetupKey(e.target.value)}
                       className="pl-9" autoComplete="off" required data-testid="admin-setup-key" />
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="admin-setup-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-primary font-semibold">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Create owner account
            </Button>
          </form>
        )}

        <p className="mt-5 text-center text-sm text-muted-foreground">
          <Link to="/admin/login" className="hover:underline">Staff &amp; Admin sign-in</Link>
        </p>
      </div>
    </div>
  );
};

export default AdminSetup;
